import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Heart, Award } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';


const QuickStats = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalDonated: 0,
    organizationsSupported: 0,
    causesSupported: 0,
    impactScore: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserStats = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch total donated amount
        const { data: donationsData, error: donationsError } = await supabase?.from('donations')?.select('amount')?.eq('user_id', user?.id);

        if (donationsError) throw donationsError;

        const totalDonated = donationsData?.reduce((sum, donation) => sum + (donation?.amount || 0), 0) || 0;

        // Fetch unique organizations supported
        const { data: orgsData, error: orgsError } = await supabase?.from('donations')?.select('organization_id')?.eq('user_id', user?.id)?.not('organization_id', 'is', null);

        if (orgsError) throw orgsError;

        const uniqueOrgs = new Set(orgsData?.map(d => d.organization_id) || []);
        const organizationsSupported = uniqueOrgs?.size;

        // Fetch unique causes supported
        const { data: causesData, error: causesError } = await supabase?.from('donations')?.select('cause_id')?.eq('user_id', user?.id)?.not('cause_id', 'is', null);

        if (causesError) throw causesError;

        const uniqueCauses = new Set(causesData?.map(d => d.cause_id) || []);
        const causesSupported = uniqueCauses?.size;

        // Fetch impact score from user profile
        const { data: profileData, error: profileError } = await supabase?.from('user_profiles')?.select('impact_score')?.eq('user_id', user?.id)?.single();

        if (profileError && profileError?.code !== 'PGRST116') throw profileError;

        const impactScore = profileData?.impact_score || 0;

        setStats({
          totalDonated,
          organizationsSupported,
          causesSupported,
          impactScore
        });
      } catch (error) {
        console.error('Error fetching user stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserStats();
  }, [user?.id]);

  const statsData = [
    {
      icon: TrendingUp,
      label: 'Total Donated',
      value: loading ? '...' : `$${stats?.totalDonated?.toLocaleString()}`,
      color: 'from-coral-500 to-orange-500',
      bgColor: 'bg-coral-50'
    },
    {
      icon: Users,
      label: 'Organizations Supported',
      value: loading ? '...' : stats?.organizationsSupported,
      color: 'from-teal-500 to-cyan-500',
      bgColor: 'bg-teal-50'
    },
    {
      icon: Heart,
      label: 'Causes Supported',
      value: loading ? '...' : stats?.causesSupported,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50'
    },
    {
      icon: Award,
      label: 'Impact Score',
      value: loading ? '...' : stats?.impactScore,
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-50'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {statsData?.map((stat, index) => {
        const Icon = stat?.icon;
        return (
          <div
            key={index}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg ${stat?.bgColor}`}>
                <Icon className={`w-6 h-6 bg-gradient-to-r ${stat?.color} bg-clip-text text-transparent`} />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">
              {stat?.value}
            </h3>
            <p className="text-sm text-gray-600">{stat?.label}</p>
          </div>
        );
      })}
    </div>
  );
};

export default QuickStats;